// Entry file
/* eslint-disable @atlaskit/editor/no-re-export */

/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { css as cssUnbounded } from '@compiled/react';
import kebabCase from 'lodash/kebabCase';
import { useIntl } from 'react-intl-next';

import Button, { type IconButtonProps } from '@atlaskit/button/new';
import { css, jsx } from '@atlaskit/css';
import type { Keymap } from '@atlaskit/editor-common/keymaps';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { type JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import VisuallyHidden from '@atlaskit/visually-hidden';

import type { EditorAgent } from '../../../utils/agents';
import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import { ButtonWithShortcut } from '../../components/ButtonWithShortcut/ButtonWithShortcut';
import { CopyIconButton } from '../../components/CopyIconButton/CopyIconButton';
import { FeedbackForm } from '../../components/FeedbackForm/FeedbackForm';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { Footer } from '../../components/Footer/Footer';
import { LatestPromptHeader } from '../../components/LatestPromptHeader/LatestPromptHeader';
import type { PromptEditor } from '../../components/PromptEditorWrapper/PromptEditorWrapper';
import { PromptForm } from '../../components/PromptForm/PromptForm';
import type {
	ClearInputMutableRefObject,
	FocusInputRefType,
} from '../../components/PromptForm/useSetInputRef';
import { RetryIconButton } from '../../components/RetryIconButton/RetryIconButton';

import { ActionButtonsLayout } from './components/ActionButtonsLayout';
import {
	RefineDropdown,
	type RefineDropdownItem,
} from './components/RefineDropdown/RefineDropdown';
import messages from './messages';

export type { RefineDropdownItem };

// Copied from packages/editor/editor-plugin-ai/src/config-items/config-items.tsx
export type ActionAppearance = 'primary' | 'secondary' | 'cancel';

/**
 * Could be either:
 * - userInput only (free form generate / interrogate)
 * - presetTitle only (summarize)
 * - both presetTitle and userInput (brainstorm)
 */
type LatestPrompt =
	| { presetTitle?: never; userADFInput?: JSONDocNode; userInput: string }
	| { presetTitle: string; userADFInput?: JSONDocNode; userInput?: string };

const previewContainerPaddingStyles = css({
	paddingTop: token('space.075'),
});

export type PreviewButton = {
	appearance: ActionAppearance;
	icon?: IconButtonProps['icon'];
	isDisabled?: boolean;
	label: string;
	onClick: () => void;
	/** Optional shortcut keymap for the action. */
	shortcut?: Keymap;
	tooltip?: string;
};

const columnLayout = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const interrogationWrapper = cssUnbounded({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& .ak-editor-content-area .ProseMirror': { margin: 0 },
	// The 0.5px is from platform/packages/editor/generative-ai-modal/src/ui/components/PromptEditorWrapper/PromptEditorWrapper.tsx
	// promptEditorWrapperOverflowStyles
	minHeight: `calc(${token('space.600')} + 0.5px)`,
	boxSizing: 'border-box',
});

const defaultPaddingStyles = css({
	paddingTop: token('space.150'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
});

const previewContainerPaddingCompact = css({ paddingTop: token('space.0') });

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const previewContainerBorders = cssUnbounded({
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
});

const previewFloatingContainerStyle = css({ display: 'flex', flexDirection: 'column' });

type Disclaimer = { text: string; tooltip: string; url?: string };

const mapActionAppearanceToButtonAppearance = {
	primary: 'primary',
	secondary: 'default',
	cancel: 'subtle',
} as const;

type Props = {
	agent?: EditorAgent;
	/* Applications are buttons that "apply" or include the generated content in the document.
  These may include options for where the user can insert or replace the content. **/
	applications: PreviewButton[];
	/**
	 * is Inline stream enabled (or should it be fallback)
	 */
	canInlineStreamChanges?: boolean;
	/**
	 * Short title for the submenu if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	childShortTitle?: string;
	clearInputRef?: ClearInputMutableRefObject;
	/**
	 * Disables user followup actions
	 *
	 * *copy, retry, insert, replace, interrogate*
	 */
	disableActions: boolean;
	disableInterrogation?: boolean;
	disclaimer?: Disclaimer;
	/**
	 * Reduces paddings and hides the horizontal rules above and below the preview container.
	 */
	hideHorizontalRule?: boolean;
	inputADFValue?: JSONDocNode;
	inputValue?: string;
	isAIFC?: boolean;
	/**
	 * a clear title for Preview Screen
	 */
	isClearTitle?: boolean;
	isInputActive: boolean;
	latestPrompt?: LatestPrompt;
	onADFChange?: (value: JSONDocNode) => void;
	onBack?: () => void;
	onBadResponseButtonClick: () => void;
	onCancel?: () => void;
	onClose?: () => void;
	onCopy?: () => void;
	onEditPromptClick?: () => void;
	onGoodResponseButtonClick: () => void;
	onInputBlur?: () => void;
	onInputChange?: (value: string) => void;
	onInputFocus?: () => void;
	onRefinementClick?: () => void;
	onRetry?: () => void;
	onSubmit?: (inputValue: string) => void;
	/**
	 * Label for the parent if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	parentPresetPromptLabel?: string;
	placeholder?: string;
	preview?: React.ReactElement;

	/**
	 * Editor to be used as input in prompt.
	 */
	PromptEditor?: PromptEditor;
	providerFactory: ProviderFactory;
	/** List of items to be shown in the Refine button dropdown menu. */
	refineDropdownItems?: RefineDropdownItem[];
	schema: Schema;
	showBack?: boolean;
	/**
	 * Show the beta lozenge in the footer.
	 */
	showBetaLozenge?: boolean;
	/**
	 * Label for a static prompt insertion
	 */
	staticInsertedPrompt?: string;
	/**
	 * Label for a static prompt
	 */
	staticPromptTitle?: string;
};

export const PreviewScreen = ({
	parentPresetPromptLabel,
	childShortTitle,
	staticPromptTitle,
	staticInsertedPrompt,
	latestPrompt,
	preview,
	disableActions = false,
	disableInterrogation = false,
	onSubmit,
	onCancel,
	onCopy,
	onRetry,
	onInputFocus,
	onInputChange,
	onADFChange,
	onGoodResponseButtonClick,
	onBadResponseButtonClick,
	onEditPromptClick,
	onBack,
	onClose,
	showBack = true,
	applications,
	isInputActive,
	inputValue,
	inputADFValue,
	PromptEditor,
	clearInputRef,
	providerFactory,
	schema,
	disclaimer,
	agent,
	onRefinementClick,
	refineDropdownItems,
	hideHorizontalRule = false,
	showBetaLozenge = false,
	isClearTitle = false,
	canInlineStreamChanges,
	isAIFC,
}: Props) => {
	const { formatMessage } = useIntl();
	const [forceFocusRing, setForceFocusRing] = useState(true);

	const presetTitleString = staticPromptTitle ?? latestPrompt?.presetTitle;

	const promptString = staticInsertedPrompt ?? latestPrompt?.userInput;

	const firePrimaryApplication = () => {
		if (!applications) {
			return;
		}
		setForceFocusRing(false);
		applications[applications.length - 1].onClick();
	};

	const onInterrogateBlur = useCallback(() => {
		setForceFocusRing(false);
	}, []);

	const handleOnInputFocus = useCallback(() => {
		setForceFocusRing(true);
		if (onInputFocus) {
			onInputFocus();
		}
	}, [onInputFocus]);

	const focusInputRef = useRef<FocusInputRefType>(undefined);

	const initialPlaceholder = agent
		? formatMessage(messages.placeholderWithAgent, { agentName: agent.name })
		: formatMessage(messages.defaultRovoInputPlaceholder);

	// This is used to determine when to disable a number of features to prevent
	// the user from performing actions on previous responses
	const isViewingLatestResponse = !disableActions && !disableInterrogation;

	const feedbackForm = useMemo(() => {
		return (
			<FeedbackForm
				onBadResponseButtonClick={onBadResponseButtonClick}
				onGoodResponseButtonClick={onGoodResponseButtonClick}
			/>
		);
	}, [onBadResponseButtonClick, onGoodResponseButtonClick]);

	const actionButtons = useMemo(() => {
		const secondaryButtons =
			!disableActions &&
			(isAIFC ? (
				<>
					{onCopy && <CopyIconButton spacing="compact" appearance="subtle" onClick={onCopy} />}
					{feedbackForm}
					{onRetry && <RetryIconButton spacing="compact" appearance="subtle" onClick={onRetry} />}
				</>
			) : (
				<>
					{feedbackForm}
					{onRetry && <RetryIconButton spacing="compact" appearance="subtle" onClick={onRetry} />}
					{onCopy && <CopyIconButton spacing="compact" appearance="subtle" onClick={onCopy} />}
				</>
			));

		const cancelButton = onCancel ? (
			<Button
				testId="discard-button"
				onClick={onCancel}
				type="button"
				appearance={disableActions ? 'primary' : 'subtle'}
				autoFocus={disableActions || applications.length === 0}
			>
				{formatMessage(messages.discardLabel)}
			</Button>
		) : undefined;

		let primaryButtons =
			!disableActions &&
			applications.map(({ onClick, label, appearance, shortcut, isDisabled, tooltip }, index) => {
				const button = (
					<ButtonWithShortcut
						key={label}
						onClick={onClick}
						type="button"
						shortcut={shortcut}
						autoFocus={appearance === 'primary'}
						forceFocusRing={index === applications.length - 1 && forceFocusRing}
						buttonAutoFocusRing={index === applications.length - 1 && !forceFocusRing}
						appearance={mapActionAppearanceToButtonAppearance[appearance]}
						isDisabled={isDisabled}
					>
						{label}
					</ButtonWithShortcut>
				);

				if (tooltip) {
					return (
						<Tooltip key={`${label}-tooltip`} content={tooltip} position="top">
							{button}
						</Tooltip>
					);
				}
				return button;
			});

		if (primaryButtons && !agent && refineDropdownItems) {
			primaryButtons.unshift(
				<RefineDropdown
					key="refine-dropdown"
					items={refineDropdownItems}
					onClick={onRefinementClick}
				/>,
			);
		}

		if (isAIFC) {
			// still brittle, assumes only 1 primary
			const primaryAction = applications.find((action) => action.appearance === 'primary');

			if (primaryAction) {
				const acceptButton = (
					<ButtonWithShortcut
						key={kebabCase(primaryAction?.label)}
						onClick={primaryAction?.onClick}
						type="button"
						shortcut={primaryAction?.shortcut}
						autoFocus
						appearance="primary"
						isDisabled={primaryAction?.isDisabled}
					>
						{formatMessage(messages.actionButtonsAccept)}
					</ButtonWithShortcut>
				);

				primaryButtons = !disableActions && [acceptButton];
			}
		}

		return (
			<ActionButtonsLayout
				cancelButton={cancelButton}
				secondaryButtons={secondaryButtons}
				primaryButtons={primaryButtons}
			/>
		);
	}, [
		disableActions,
		feedbackForm,
		onRetry,
		onCopy,
		onCancel,
		applications,
		formatMessage,
		agent,
		refineDropdownItems,
		isAIFC,
		forceFocusRing,
		onRefinementClick,
	]);

	const generatedContentPreview = (
		<div
			css={[
				defaultPaddingStyles,
				hideHorizontalRule
					? previewContainerPaddingCompact
					: isAIFC
						? undefined
						: previewContainerBorders,
			]}
		>
			<Stack space="space.200">
				<div css={isAIFC && [previewContainerPaddingStyles]}>{preview}</div>
				{actionButtons}
			</Stack>
		</div>
	);

	const promptForm = isViewingLatestResponse && PromptEditor && (
		<div css={[defaultPaddingStyles, interrogationWrapper]}>
			{isViewingLatestResponse && (
				<PromptForm
					type="interrogate"
					showButtons={isInputActive}
					autoFocus={true}
					placeholder={`${initialPlaceholder}${isAIFC ? '...' : ''}`}
					value={inputValue}
					adfValue={inputADFValue}
					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					onFormSubmit={onSubmit!}
					onInputChange={onInputChange}
					onADFChange={onADFChange}
					onInputFocus={handleOnInputFocus}
					focusInputRef={focusInputRef}
					clearInputRef={clearInputRef}
					PromptEditor={PromptEditor}
					enableLinks={true}
					fireParentActionButton={firePrimaryApplication}
					focusParentActionButton={setForceFocusRing}
					showLogo={false}
					onInputBlur={onInterrogateBlur}
					isAIFC={isAIFC}
				/>
			)}
		</div>
	);

	return (
		<div
			css={[columnLayout]}
			/* eslint-disable @atlaskit/ui-styling-standard/no-classname-prop */
			className="preview-screen"
			/* eslint-enable */
		>
			<FloatingContainer
				disablePadding
				data-testid="preview-screen"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={`preview-screen ${AI_MODAL_SCREEN_CLASS}`}
				rainbowBorder={false}
				css={previewFloatingContainerStyle}
				isAIFC={isAIFC}
			>
				<VisuallyHidden>{formatMessage(messages.previewScreenLabel)}</VisuallyHidden>
				{!isAIFC && (
					<LatestPromptHeader
						parentPresetPromptLabel={parentPresetPromptLabel}
						childShortTitle={childShortTitle}
						prompt={promptString}
						promptADF={latestPrompt?.userADFInput}
						presetTitle={presetTitleString}
						agent={agent}
						providerFactory={providerFactory}
						schema={schema}
						onEditPromptClick={onEditPromptClick}
						showBack={showBack}
						onBack={onBack}
						onClose={onClose}
						isClearTitle={isClearTitle}
					/>
				)}
				{generatedContentPreview}
				{!isAIFC && promptForm}
				<Footer
					brand={agent ? 'rovo' : 'atlassian-intelligence'}
					disclaimer={disclaimer}
					showDisclaimer={true}
					showBetaLozenge={showBetaLozenge}
					showFeedback={isAIFC && canInlineStreamChanges}
					feedbackForm={feedbackForm}
					isAIFC={isAIFC}
				/>
			</FloatingContainer>
		</div>
	);
};
